import { Avatar, Button, Col, Row, Skeleton, Typography, Card, Space, Badge } from 'antd';
import { EditOutlined, UserOutlined, SyncOutlined } from '@ant-design/icons';
import { CSSProperties, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { AppContext } from 'context/AppContext';
import FollowModal from 'components/Follow/FollowModal';
import { FollowType } from 'services/types/FollowTypes';
import { useUserStats, useUserLinks, useUserEnrollments, useUserDetails } from 'services/UserService';
import EditDrawer, { EditType } from './components/EditDrawer';
import UserStatsSection from './components/UserStatsSection';
import SocialMediaSection from './components/SocialMediaSection';
import ValuesSection from './components/ValuesSection';
import PersonalitySection from './components/PersonalitySection';
import PersonalInfoSection from './components/PersonalInfoSection';
import InterestsSection from './components/InterestsSection';
import MemberOfSection from './components/MemberOfSection';
import styles from './Profile.module.css';

const { Title, Text, Paragraph } = Typography;

interface ProfileProps {
  userId?: string;
}

const Profile: React.FC<ProfileProps> = ({ userId: propUserId }) => {
  const { t } = useTranslation();
  const { commonData, authentication, api } = useContext(AppContext);
  const { userDetails } = commonData;
  const { authKey } = authentication;
  const { userId: urlUserId } = useParams<{ userId: string }>();

  const userId = propUserId || urlUserId;

  const [editType, setEditType] = useState(EditType.None);
  const [followType, setFollowType] = useState<FollowType>();
  const [followButtonLoading, setFollowButtonLoading] = useState(false);
  const [isPolishingBio, setIsPolishingBio] = useState(false);
  const [bioActionType, setBioActionType] = useState<'polish' | 'generation'>('polish');

  const isOwnProfile = !userId || userId === userDetails?.id;
  const targetUserId = userId || userDetails?.id || '';

  const {
    data: profileUserDetails,
    isLoading: isLoadingProfile
  } = useUserDetails(targetUserId, authKey || '', !isOwnProfile);

  const isLoading = isOwnProfile ? !userDetails : isLoadingProfile;

  const {
    data: userStats,
    isLoading: isStatsLoading
  } = useUserStats(targetUserId, authKey || '');

  const {
    data: userLinks,
    isLoading: isLinksLoading
  } = useUserLinks(targetUserId, authKey || '');

  const {
    data: userEnrollments,
    isLoading: isEnrollmentsLoading
  } = useUserEnrollments(targetUserId, authKey || '', !isOwnProfile);

  const displayUserDetails = isOwnProfile ? userDetails : profileUserDetails;

  const editAvatarHandler = useCallback(() => setEditType(EditType.Avatar), []);
  const editBioHandler = useCallback(() => setEditType(EditType.Bio), []);
  const editNameHandler = useCallback(() => setEditType(EditType.Name), []);
  const editUsernameHandler = useCallback(() => setEditType(EditType.Username), []);
  const closeHandler = useCallback(() => setEditType(EditType.None), []);

  const hasFollower = (displayUserDetails?.profile?.follower_count ?? 0) > 0;
  const hasFollowing = (displayUserDetails?.profile?.following_count ?? 0) > 0;

  const followerStyle: CSSProperties | undefined = hasFollower ? { cursor: 'pointer' } : undefined;
  const followingStyle: CSSProperties | undefined = hasFollowing ? { cursor: 'pointer' } : undefined;

  const openFollowerModal = useCallback(() => {
    if (hasFollower) {
      setFollowType('Follower');
    }
  }, [hasFollower, setFollowType]);

  const openFollowingModal = useCallback(() => {
    if (hasFollowing) {
      setFollowType('Following');
    }
  }, [hasFollowing, setFollowType]);

  const closeFollowModal = useCallback(() => {
    setFollowType(undefined);
  }, [setFollowType]);

  const handlePolishBio = useCallback(() => {
    if (!userDetails?.id || !userDetails?.profile?.bio) return;
    
    setBioActionType('polish');
    setIsPolishingBio(true);
    setEditType(EditType.Bio);
  }, [userDetails]);

  const handleGenerateBio = useCallback(() => {
    if (!userDetails?.id) return;
    
    setBioActionType('generation');
    setIsPolishingBio(true);
    setEditType(EditType.Bio);
  }, [userDetails]);

  const handlePolishComplete = useCallback((polishedBio: string) => {
    setIsPolishingBio(false);
  }, []);

  const handleFollow = useCallback(async () => {
    if (!displayUserDetails) return;

    const isFollowing = displayUserDetails.profile?.is_following ?? false;

    setFollowButtonLoading(true);
    try {
      let isSuccess = false;
      if (isFollowing) {
        const { error = {} } = await api.profile.unfollowUser(displayUserDetails.id ?? '');
        isSuccess = isEmpty(error);
      } else {
        const { error = {} } = await api.profile.followUser(displayUserDetails.id ?? '');
        isSuccess = isEmpty(error);
      }

      if (isSuccess && displayUserDetails.profile) {
        displayUserDetails.profile.is_following = !isFollowing;
        // Update follower count
        if (isFollowing) {
          displayUserDetails.profile.follower_count = (displayUserDetails.profile.follower_count || 0) - 1;
        } else {
          displayUserDetails.profile.follower_count = (displayUserDetails.profile.follower_count || 0) + 1;
        }
      }
    } catch (error) {
      console.error('Error following/unfollowing user:', error);
    } finally {
      setFollowButtonLoading(false);
    }
  }, [displayUserDetails, api]);

  const getValues = (): string[] => {
    let values: string[] = [];
    const profile = displayUserDetails?.profile;

    if (profile?.value_sustainable_goals) {
      values = values.concat(profile.value_sustainable_goals.map((value: any) => `value_sustainable_goals.${value}`));
    }
    if (profile?.value_priorities) {
      values = values.concat(profile.value_priorities.map((value: any) => `value_priorities.${value}`));
    }

    return values;
  };

  const getPersonalities = (): string[] => {
    const profile = displayUserDetails?.profile;
    return (profile?.personalities || []).map((value: any) => `personalities.${value}`);
  };

  const getInterests = (): string[] => {
    let values: string[] = [];
    const profile = displayUserDetails?.profile;

    if (profile?.interest_food_n_drinks) {
      values = values.concat(profile.interest_food_n_drinks.map((value: any) => `interest_food_n_drinks.${value}`));
    }
    if (profile?.interest_sports) {
      values = values.concat(profile.interest_sports.map((value: any) => `interest_sports.${value}`));
    }
    if (profile?.interest_musics) {
      values = values.concat(profile.interest_musics.map((value: any) => `interest_musics.${value}`));
    }
    if (profile?.interest_movie_n_tvs) {
      values = values.concat(profile.interest_movie_n_tvs.map((value: any) => `interest_movie_n_tvs.${value}`));
    }
    if (profile?.interest_readings) {
      values = values.concat(profile.interest_readings.map((value: any) => `interest_readings.${value}`));
    }
    if (profile?.interest_staying_ins) {
      values = values.concat(profile.interest_staying_ins.map((value: any) => `interest_staying_ins.${value}`));
    }
    if (profile?.interest_going_outs) {
      values = values.concat(profile.interest_going_outs.map((value: any) => `interest_going_outs.${value}`));
    }
    if (profile?.interest_creativities) {
      values = values.concat(profile.interest_creativities.map((value: any) => `interest_creativities.${value}`));
    }
    if (profile?.interest_explorings) {
      values = values.concat(profile.interest_explorings.map((value: any) => `interest_explorings.${value}`));
    }
    if (profile?.interest_pets) {
      values = values.concat(profile.interest_pets.map((value: any) => `interest_pets.${value}`));
    }

    return values;
  };

  // Check if sections have data (for non-own profiles)
  const hasBio = isOwnProfile || !!displayUserDetails?.profile?.bio;
  const hasValues = isOwnProfile || getValues().length > 0;
  const hasPersonalities = isOwnProfile || getPersonalities().length > 0;
  const hasInterests = isOwnProfile || getInterests().length > 0;
  const hasSocialLinks = isOwnProfile || (userLinks && userLinks.length > 0);

  // Follow button state
  const isFollowing = displayUserDetails?.profile?.is_following ?? false;

  // Check if name or username is missing
  const hasFullName = displayUserDetails?.first_name || displayUserDetails?.last_name;
  const hasUsername = displayUserDetails?.username;

  return (
    <div className={styles.profileContainer}>
      {/* Header Card */}
      <Card className={styles.profileHeaderCard} bordered={false}>
        <Row gutter={[24, 24]} align="middle">
          <Col>
            <div className={styles.avatarContainer}>
              <Skeleton
                loading={isLoading}
                avatar={{ shape: 'circle', size: 120 }}
                title={false}
                paragraph={false}
                active
              >
                <Badge dot={!isLoading} status="success" offset={[-8, 8]}>
                  {displayUserDetails?.profile?.image_small_url ? (
                    <Avatar
                      src={displayUserDetails?.profile?.image_small_url}
                      size={120}
                      className={styles.profileAvatar}
                      onClick={isOwnProfile ? editAvatarHandler : undefined}
                      style={{ cursor: isOwnProfile ? 'pointer' : 'default' }}
                    />
                  ) : (
                    <Avatar
                      icon={<UserOutlined />}
                      size={120}
                      className={styles.profileAvatar}
                      onClick={isOwnProfile ? editAvatarHandler : undefined}
                      style={{ cursor: isOwnProfile ? 'pointer' : 'default' }}
                    />
                  )}
                  {isOwnProfile && (
                    <Button
                      type="primary"
                      shape="circle"
                      size="small"
                      icon={<EditOutlined />}
                      className={styles.avatarEditButton}
                      onClick={editAvatarHandler}
                    />
                  )}
                </Badge>
              </Skeleton>
            </div>
          </Col>

          <Col flex="auto">
            <Space direction="vertical" size={4} className={styles.profileHeaderInfo}>
              <Skeleton loading={isLoading} paragraph={false} active>
                <div className={styles.nameSection}>
                  {hasFullName ? (
                    <>
                      <Title level={2} className={styles.profileName}>
                        {displayUserDetails?.first_name} {displayUserDetails?.last_name}
                      </Title>
                      {isOwnProfile && (
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={editNameHandler}
                          className={styles.editButton}
                        />
                      )}
                    </>
                  ) : (
                    isOwnProfile && (
                      <Button
                        type="primary"
                        onClick={editNameHandler}
                        className={styles.addInfoButton}
                        icon={<EditOutlined />}
                      >
                        {t('#Profile.add_name')}
                      </Button>
                    )
                  )}
                </div>
              </Skeleton>

              <Skeleton loading={isLoading} title={{ width: 100 }} paragraph={false} active>
                <div className={styles.usernameSection}>
                  {hasUsername ? (
                    <>
                      <Text className={styles.username}>@{displayUserDetails?.username}</Text>
                      {isOwnProfile && (
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={editUsernameHandler}
                          className={styles.editButton}
                        />
                      )}
                    </>
                  ) : (
                    isOwnProfile && (
                      <Button
                        type="primary"
                        onClick={editUsernameHandler}
                        className={styles.addInfoButton}
                        icon={<EditOutlined />}
                      >
                        {t('#Profile.add_username')}
                      </Button>
                    )
                  )}
                </div>
              </Skeleton>

              {/* Enhanced Stats Row */}
              <div className={styles.statsRow}>
                <div className={styles.statItem} style={followerStyle} onClick={openFollowerModal}>
                  <Skeleton loading={isLoading} paragraph={false} active>
                    <div className={styles.statNumber}>{displayUserDetails?.profile?.follower_count || 0}</div>
                    <div className={styles.statLabel}>{t('#Profile.followers')}</div>
                  </Skeleton>
                </div>
                <div className={styles.statItem} style={followingStyle} onClick={openFollowingModal}>
                  <Skeleton loading={isLoading} paragraph={false} active>
                    <div className={styles.statNumber}>{displayUserDetails?.profile?.following_count || 0}</div>
                    <div className={styles.statLabel}>{t('#Profile.following')}</div>
                  </Skeleton>
                </div>
                <div className={styles.statItem}>
                  <Skeleton loading={isLoading} paragraph={false} active>
                    <div className={styles.statNumber}>{displayUserDetails?.profile?.liked_count || 0}</div>
                    <div className={styles.statLabel}>{t('#Profile.liked')}</div>
                  </Skeleton>
                </div>
              </div>
            </Space>
          </Col>

          {/* Follow Button for non-own profiles */}
          {!isOwnProfile && (
            <Col>
              <div className={styles.followButtonContainer}>
                <Button
                  type="default"
                  size="large"
                  onClick={handleFollow}
                  className={`${styles.followButton} ${isFollowing ? styles.following : ''}`}
                  loading={followButtonLoading || isLoading}
                >
                  {isFollowing ? t('#Profile.following') : t('#Profile.follow')}
                </Button>
              </div>
            </Col>
          )}
        </Row>
      </Card>

      {/* Compact User Stats Section */}
      <UserStatsSection
        userStats={userStats}
        isLoading={isStatsLoading || isLoading}
      />

      {/* Bio Section - Only show if has bio or is own profile */}
      {hasBio && (
        <Card className={styles.sectionCard} bordered={false}>
          <div className={styles.sectionHeader}>
            <Title level={4} className={styles.sectionTitle}>{t('#Profile.bio')}</Title>
            {isOwnProfile && displayUserDetails?.profile?.bio && (
              <Space className={styles.desktopButtons}>
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  onClick={handlePolishBio}
                  className={styles.polishBioButton}
                  title={t('#Profile.polish_bio')}
                  disabled={isPolishingBio}
                >
                  {t('#Profile.polish_bio')}
                </Button>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={editBioHandler}
                  className={styles.sectionEditButton}
                />
              </Space>
            )}
          </div>
          <Skeleton loading={isLoading} title={false} paragraph={{ rows: 3 }} active>
            {displayUserDetails?.profile?.bio ? (
              <>
                <Paragraph className={styles.bioText}>
                  {displayUserDetails.profile.bio}
                </Paragraph>
                {isOwnProfile && (
                  <div className={styles.mobileButtons}>
                    <Button
                      type="primary"
                      icon={<SyncOutlined />}
                      onClick={handlePolishBio}
                      className={styles.mobilePolishButton}
                      disabled={isPolishingBio}
                      block
                    >
                      {t('#Profile.polish_bio')}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              isOwnProfile && (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Button
                    type="primary"
                    icon={<SyncOutlined />}
                    onClick={handleGenerateBio}
                    className={styles.generateBioButton}
                    disabled={isPolishingBio}
                    block
                  >
                    {t('#Profile.generate_bio_with_ai')}
                  </Button>
                  <Button
                    type="dashed"
                    onClick={editBioHandler}
                    className={styles.addBioButton}
                  >
                    {t('#Profile.add_bio')}
                  </Button>
                </Space>
              )
            )}
          </Skeleton>
        </Card>
      )}

      {/* Edit Drawer - Only for own profile */}
      {isOwnProfile && (
        <EditDrawer
          type={editType}
          closeHandler={closeHandler}
          isPolishingBio={isPolishingBio}
          bioActionType={bioActionType}
          onPolishComplete={handlePolishComplete}
        />
      )}

      <FollowModal
        userId={displayUserDetails?.id}
        type={followType}
        closeHandler={closeFollowModal}
      />

      {/* Social Media Section - Only show if has links or is own profile */}
      {hasSocialLinks && (
        <SocialMediaSection
          userLinks={userLinks}
          isLoading={isLinksLoading || isLoading}
        />
      )}

      {/* Values Section - Only show if has values or is own profile */}
      {hasValues && (
        <ValuesSection
          values={getValues()}
          isLoading={isLoading}
        />
      )}

      {/* Personality Section - Only show if has personalities or is own profile */}
      {hasPersonalities && (
        <PersonalitySection
          personalities={getPersonalities()}
          isLoading={isLoading}
        />
      )}

      {/* Interests Section - Only show if has interests or is own profile */}
      {hasInterests && (
        <InterestsSection
          interests={getInterests()}
          isLoading={isLoading}
        />
      )}

      {/* Personal Information Section - Only for own profile */}
      {isOwnProfile && (
        <PersonalInfoSection
          gender={displayUserDetails?.profile?.gender}
          dateOfBirth={displayUserDetails?.profile?.date_of_birth}
          phoneCode={displayUserDetails?.profile?.phone_code}
          isLoading={isLoading}
        />
      )}

      {/* Member of Section - Only for non-own profiles */}
      {!isOwnProfile && (
        <MemberOfSection
          enrollments={userEnrollments}
          isLoading={isEnrollmentsLoading || isLoading}
          authKey={authKey || ''}
          userId={targetUserId}
        />
      )}
    </div>
  );
};

export default Profile;